<template>
  <text-input
    v-if="type === 'text'"
    v-bind="$props"
    :label="`${field.title} ${field.required ? '*' : ''}`"
    :value="value"
    :disabled="disabled"
    :readonly="readonly"
    hide-details="auto"
    v-on="$listeners"
    @input="value => $emit('input', value)"
  />

  <text-input
    v-else-if="type === 'number'"
    v-bind="$props"
    :label="`${field.title} ${field.required ? '*' : ''}`"
    :value="value"
    :disabled="disabled"
    :readonly="readonly"
    type="number"
    hide-details="auto"
    v-on="$listeners"
    @input="value => $emit('input', value)"
  />

  <toggle-input
    v-else-if="type === 'boolean'"
    v-bind="$props"
    :label="`${field.title} ${field.required ? '*' : ''}`"
    :input-value="value"
    :value="value"
    :disabled="disabled"
    :readonly="readonly"
    color="primary"
    hide-details="auto"
    v-on="$listeners"
    @change="value => $emit('input', value)"
  />

  <textarea-input
    v-else-if="type === 'textarea'"
    v-bind="$props"
    :value="value"
    :disabled="disabled"
    :readonly="readonly"
    rows="3"
    auto-grow
    hide-details="auto"
    v-on="$listeners"
    @input="value => $emit('input', value)"
  />

  <froala-input
    v-else-if="type === 'froala'"
    v-bind="$props"
    :value="value"
    :disabled="disabled"
    :readonly="readonly"
    :toolbar-buttons="froalaToolbarButtons"
    hide-details="auto"
    v-on="$listeners"
    @input="value => $emit('input', value)"
  />

  <select-image
    v-else-if="type === 'image'"
    v-bind="$props"
    :is-disabled="false"
    :title="`${field.title} ${field.required ? '*' : ''}`"
    :size="250"
    :label="`${field.title} ${field.required ? '*' : ''}`"
    :value="value"
    :disabled="disabled"
    :readonly="readonly"
    show-extra-previews
    hide-details="auto"
    v-on="$listeners"
    @input="value => $emit('input', value)"
  />

  <text-input
    v-else-if="type === 'hidden'"
    v-bind="$props"
    :label="`${field.title} ${field.required ? '*' : ''}`"
    :value="value"
    :disabled="disabled"
    :readonly="readonly"
    type="hidden"
    hide-details="auto"
    v-on="$listeners"
    @input="value => $emit('input', value)"
  />

  <select-input
    v-else-if="type === 'select'"
    v-bind="$props"
    :items="options"
    :disabled="disabled"
    :readonly="readonly"
    menu-props="auto"
    item-text="text"
    item-value="value"
    width="auto"
    hide-details="auto"
    v-on="$listeners"
    @input="value => $emit('input', value)"
  />

  <div v-else-if="type === 'flexible'">
    <p class="font-weight-bold text-subtitle-2 mb-2">{{ field.title }}</p>
    <v-card v-for="(data, key) in value" :key="key" outlined class="py-4 px-4 mb-4">
      <template v-for="(item, index) in field.fields || []">
        <div :key="index">
          <page-module-editor
            v-model="data[item.key]"
            :field="item"
            :type="item.type"
            :label="item.title"
            :disabled="disabled"
            :readonly="readonly"
            :select-options="selectOptions"
          />
          <v-divider v-if="index !== field.fields.length - 1" class="mb-4" />
        </div>
      </template>
      <div v-if="!disabled" class="d-flex justify-end">
        <v-btn color="red" dark depressed @click="removeItem(key)">
          <v-icon left>delete</v-icon>
          <span>Remove</span>
        </v-btn>
      </div>
    </v-card>
    <v-btn v-if="!disabled" color="primary" depressed @click="addItem">Add More</v-btn>
  </div>

  <pre v-else>{{ field }}</pre>
</template>

<script>
import TextInput from '@/components/forms/text-input'
import SelectInput from '@/components/forms/select-list'
import ToggleInput from '@/components/forms/toggle-input'
import TextareaInput from '@/components/forms/textarea-input'

import { VCard, VBtn } from 'vuetify/lib'
import FroalaInput from '@/components/forms/froala-input'
import SelectImage from '@/components/forms/select-image'

export default {
  name: 'PageModuleEditor',
  components: {
    TextInput,
    SelectInput,
    ToggleInput,
    TextareaInput,
    FroalaInput,
    SelectImage,
    VCard,
    VBtn,
  },
  props: {
    value: {},
    field: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    label: {
      type: [String, null],
      default: null,
    },
    module: {
      type: Object,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    selectOptions: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    froalaToolbarButtons() {
      return () => [
        'paragraphFormat',
        'align',
        'bold',
        'underline',
        'italic',
        'strikeThrough',
        'clearFormatting',
        'insertTable',
        'insertVideo',
        'formatOL',
        'formatUL',
        'mediaGallery',
        'customLinkInsert',
        'shortcodeInsert',
        'html',
      ]
    },
    options() {
      if (!!this.field.dynamic && !!this.field.dynamicKey) return this.selectOptions[this.field.dynamicKey] || []
      return this.field.options || []
    },
  },
  methods: {
    removeItem(index) {
      this.value.splice(index, 1)
    },
    addItem() {
      this.value.push(
        this.field.fields.reduce((previousValue, currentValue) => {
          previousValue[currentValue.key] = ''
          return previousValue
        }, {})
      )
    },
  },
}
</script>
